import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  type RegenerateAffectedDto,
  type SlideDto,
  type SlideVersionDto,
  type WhereUsedDto,
} from '@pb/shared';
import {
  getSlide,
  listSlideVersions,
  retireSlide,
  slideVersionFileUrl,
  unretireSlide,
  updateSlide,
  uploadSlideVersion,
} from '../api/slides';
import { listEvents } from '../api/events';
import { getWhereUsed, regenerateAffected } from '../api/whereUsed';
import { TypedConfirmDialog } from '../components/ConfirmDialogs';
import { Dialog } from '../components/Dialog';
import { FilePicker, ProgressBar } from '../components/FilePicker';
import { RegenerateResults } from '../components/Generations';
import { HistoryList } from '../components/History';
import { Tabs } from '../components/Nav';
import { DataView, EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { TagEditorDialog, TagList } from '../components/Tags';
import { Thumb } from '../components/Thumb';
import { WhereUsedList, WhereUsedPanel } from '../components/WhereUsed';
import { useDetailCrumb, useProject, useProjectOwnerGuard } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { originLabel, pluralize } from '../lib/text';
import { usePolling, useResource, type Resource } from '../lib/useResource';

type SlideTab = 'pages' | 'versions' | 'compare' | 'history';

export function InlineTitle({
  value,
  label,
  onSave,
}: {
  value: string;
  label: string;
  onSave: (value: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!editing) {
    return (
      <div className="row">
        <h2>{value}</h2>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
          aria-label={`Rename ${label}`}
        >
          Rename
        </button>
      </div>
    );
  }

  const save = async () => {
    if (!draft.trim()) return;
    if (draft.trim() === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <input
          className="input"
          style={{ minWidth: 420, fontSize: 'var(--text-lg)' }}
          value={draft}
          maxLength={200}
          aria-label={`${label} name`}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditing(false);
          }}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.trim()}>
          Save
        </button>
        <button type="button" className="btn btn-sm" onClick={() => setEditing(false)} disabled={busy}>
          Cancel
        </button>
      </form>
      <ErrorAlert error={error} />
    </div>
  );
}

function PagesView({ version, onRetried }: { version: SlideVersionDto; onRetried: () => void }) {
  return (
    <div className="stack">
      {version.pages.map((page) => (
        <div key={page.page_no} className="card" style={{ padding: 16 }}>
          <div className="two-col-even" style={{ gridTemplateColumns: '420px 1fr' }}>
            <Thumb thumb={page.thumbnail} alt={`Page ${page.page_no}`} onRetried={onRetried} />
            <div className="stack">
              <h4>Page {page.page_no}</h4>
              <div>
                <div className="field-label">Text</div>
                <div className="pre small">{page.text || <span className="faint">No text.</span>}</div>
              </div>
              <div>
                <div className="field-label">Speaker notes</div>
                <div className="pre small muted">{page.notes || <span className="faint">No notes.</span>}</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function VersionMeta({ version }: { version: SlideVersionDto }) {
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="row-wrap">
        <strong>Version {version.version_no}</strong>
        <span className="badge">{originLabel(version.origin)}</span>
        <span className="badge">{pluralize(version.page_count, 'page')}</span>
        {version.changed_after_event ? (
          <span className="badge badge-accent">
            Changed after {version.changed_after_event.title} ({formatDate(version.changed_after_event.date)})
          </span>
        ) : null}
      </div>
      <div className="small muted">
        {formatDateTime(version.created_at)}
        {version.author ? ` · ${version.author}` : ''}
      </div>
      {version.note ? <div className="small pre">{version.note}</div> : <div className="small faint">No change note.</div>}
    </div>
  );
}

function VersionsView({ versions, onRetried }: { versions: SlideVersionDto[]; onRetried: () => void }) {
  return (
    <div className="stack">
      {versions.map((version) => (
        <div key={version.id} className="card" style={{ padding: 16 }}>
          <div className="row-between" style={{ alignItems: 'flex-start' }}>
            <VersionMeta version={version} />
            <a className="btn btn-sm" href={slideVersionFileUrl(version.id)} download>
              Download .pptx
            </a>
          </div>
          <div className="version-pages" style={{ marginTop: 12 }}>
            {version.pages.map((page) => (
              <Thumb
                key={page.page_no}
                thumb={page.thumbnail}
                alt={`Version ${version.version_no}, page ${page.page_no}`}
                size="sm"
                onRetried={onRetried}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CompareView({ versions, onRetried }: { versions: SlideVersionDto[]; onRetried: () => void }) {
  const sorted = [...versions].sort((a, b) => b.version_no - a.version_no);
  const [left, setLeft] = useState<number | undefined>(sorted[1]?.id ?? sorted[0]?.id);
  const [right, setRight] = useState<number | undefined>(sorted[0]?.id);
  if (sorted.length < 2) {
    return <EmptyState title="Only one version">Upload a new version to compare two versions side by side.</EmptyState>;
  }
  const columns: [number | undefined, (id: number) => void, string][] = [
    [left, setLeft, 'compare-left'],
    [right, setRight, 'compare-right'],
  ];
  return (
    <div className="compare-cols">
      {columns.map(([value, set, id]) => {
        const version = sorted.find((v) => v.id === value);
        return (
          <div key={id} className="stack">
            <div className="field">
              <label htmlFor={id}>{id === 'compare-left' ? 'Left version' : 'Right version'}</label>
              <select id={id} className="select" value={value} onChange={(event) => set(Number(event.target.value))}>
                {sorted.map((v) => (
                  <option key={v.id} value={v.id}>
                    Version {v.version_no} — {formatDate(v.created_at)}
                  </option>
                ))}
              </select>
            </div>
            {version ? (
              <>
                <VersionMeta version={version} />
                {version.pages.map((page) => (
                  <Thumb
                    key={page.page_no}
                    thumb={page.thumbnail}
                    alt={`Version ${version.version_no}, page ${page.page_no}`}
                    badge={`Page ${page.page_no}`}
                    onRetried={onRetried}
                  />
                ))}
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function UploadVersionDialog({
  slide,
  whereUsed,
  onClose,
  onSaved,
}: {
  slide: SlideDto;
  whereUsed: Resource<WhereUsedDto>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { project } = useProject();
  const events = useResource(() => listEvents(project.id), [project.id]);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [eventId, setEventId] = useState<number | ''>('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenResult, setRegenResult] = useState<RegenerateAffectedDto | null>(null);

  const upload = async () => {
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      await uploadSlideVersion(slide.id, file, note, eventId === '' ? null : eventId, setProgress).promise;
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setProgress(null);
    }
  };

  const regenerate = async () => {
    setRegenBusy(true);
    setError(null);
    try {
      setRegenResult(await regenerateAffected('slide', slide.id));
    } catch (err) {
      setError(err);
    } finally {
      setRegenBusy(false);
    }
  };

  const busy = progress !== null || regenBusy;
  const affected = whereUsed.data ? whereUsed.data.presentations.length + whereUsed.data.events.length : 0;
  const sortedEvents = [...(events.data ?? [])].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Dialog
      title={saved ? 'New version saved' : 'Upload a new version'}
      wide
      onClose={onClose}
      closeDisabled={busy}
      footer={
        saved ? (
          <>
            {regenResult ? null : (
              <button
                type="button"
                className="btn"
                onClick={() => void regenerate()}
                disabled={regenBusy || affected === 0}
              >
                {regenBusy ? 'Regenerating…' : 'Regenerate affected'}
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose} disabled={regenBusy}>
              Done
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void upload()} disabled={!file || busy}>
              {progress !== null ? 'Uploading…' : 'Save new version'}
            </button>
          </>
        )
      }
    >
      {saved ? (
        <>
          <div className="alert alert-success">
            The new version of “{slide.title}” is saved. Every presentation that uses this slide shows it from now on.
          </div>
          {regenResult ? (
            <RegenerateResults result={regenResult} />
          ) : affected > 0 ? (
            <p>
              Regenerate the {pluralize(affected, 'affected presentation or planned event', 'affected presentations and planned events')}{' '}
              now to get updated files. Held events are never touched.
            </p>
          ) : (
            <p className="muted">No presentation or planned event uses this slide, so nothing needs regenerating.</p>
          )}
          {regenBusy ? <Loading label="Regenerating…" /> : null}
        </>
      ) : (
        <>
          <FilePicker
            file={file}
            onFile={(f) => {
              setFile(f);
              setError(null);
            }}
            disabled={busy}
          />
          {progress !== null ? <ProgressBar value={progress} /> : null}
          <div className="form-grid">
            <div className="field span-2">
              <label htmlFor="version-note">Change note</label>
              <textarea
                id="version-note"
                className="textarea"
                value={note}
                maxLength={2000}
                onChange={(event) => setNote(event.target.value)}
                placeholder="What changed, and why"
                disabled={busy}
              />
            </div>
            <div className="field span-2">
              <label htmlFor="version-event">Changed after event (optional)</label>
              <select
                id="version-event"
                className="select"
                value={eventId}
                onChange={(event) => setEventId(event.target.value ? Number(event.target.value) : '')}
                disabled={busy || !events.data}
              >
                <option value="">None</option>
                {sortedEvents.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title} — {formatDate(e.date)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="panel" style={{ padding: 16 }}>
            <h4 style={{ marginBottom: 8 }}>This slide is used in</h4>
            {whereUsed.error ? (
              <ErrorState error={whereUsed.error} onRetry={() => void whereUsed.reload()} />
            ) : whereUsed.data ? (
              <WhereUsedList data={whereUsed.data} />
            ) : (
              <Loading />
            )}
          </div>
        </>
      )}
      <ErrorAlert error={error} />
    </Dialog>
  );
}

export function SlidePage() {
  const { slideId } = useParams();
  const id = Number(slideId);
  const slide = useResource(() => getSlide(id), [id]);
  const versions = useResource(() => listSlideVersions(id), [id]);
  const whereUsed = useResource(() => getWhereUsed('slide', id), [id]);
  const mismatched = useProjectOwnerGuard(slide.data?.project_id);
  const [tab, setTab] = useState<SlideTab>('pages');
  const [dialog, setDialog] = useState<'upload' | 'tags' | 'retire' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useDetailCrumb(slide.data?.title);

  const pending =
    anyPending((slide.data?.latest.pages ?? []).map((p) => p.thumbnail)) ||
    anyPending((versions.data ?? []).flatMap((v) => v.pages.map((p) => p.thumbnail)));
  usePolling(pending, () => {
    void slide.refresh();
    void versions.refresh();
  });

  const refreshAll = () => {
    void slide.refresh();
    void versions.refresh();
    void whereUsed.refresh();
  };

  const unretire = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await unretireSlide(id);
      slide.setData(updated);
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DataView resource={slide} what="slide" loadingLabel="Loading slide…">
      {(s) => {
        if (mismatched) return <Loading label="Loading slide…" />;
        return (
        <div className="section">
          <div className="page-header">
            <div className="page-title">
              <InlineTitle
                value={s.title}
                label="slide"
                onSave={async (title) => {
                  await updateSlide(s.id, title);
                  await slide.refresh();
                }}
              />
              <div className="row-wrap">
                {s.retired ? <span className="badge badge-warning">Retired</span> : null}
                <span className="badge">Version {s.version_no}</span>
                <span className="badge">{pluralize(s.page_count, 'page')}</span>
                <span className="small faint">Last change {formatDate(s.updated_at)}</span>
              </div>
              <div className="row-wrap">
                <TagList tags={s.tags} />
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDialog('tags')}>
                  {s.tags.length ? 'Edit tags' : 'Add tags'}
                </button>
              </div>
            </div>
            <div className="page-actions">
              <a className="btn" href={slideVersionFileUrl(s.latest.id)} download>
                Download latest
              </a>
              <button type="button" className="btn btn-primary" onClick={() => setDialog('upload')}>
                Upload new version
              </button>
              {s.retired ? (
                <button type="button" className="btn" onClick={() => void unretire()} disabled={busy}>
                  Unretire
                </button>
              ) : (
                <button type="button" className="btn" onClick={() => setDialog('retire')}>
                  Retire
                </button>
              )}
            </div>
          </div>
          <ErrorAlert error={actionError} />
          <div className="two-col">
            <div className="stack">
              <Tabs
                label="Slide views"
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: 'pages', label: 'Pages' },
                  { key: 'versions', label: `Versions${versions.data ? ` (${versions.data.length})` : ''}` },
                  { key: 'compare', label: 'Compare' },
                  { key: 'history', label: 'History' },
                ]}
              />
              {tab === 'pages' ? <PagesView version={s.latest} onRetried={() => void slide.refresh()} /> : null}
              {tab === 'versions' || tab === 'compare' ? (
                versions.error ? (
                  <ErrorState error={versions.error} onRetry={() => void versions.reload()} />
                ) : versions.data === undefined ? (
                  <Loading />
                ) : tab === 'versions' ? (
                  <VersionsView
                    versions={[...versions.data].sort((a, b) => b.version_no - a.version_no)}
                    onRetried={() => void versions.refresh()}
                  />
                ) : (
                  <CompareView versions={versions.data} onRetried={() => void versions.refresh()} />
                )
              ) : null}
              {tab === 'history' ? <HistoryList target="slide" id={s.id} /> : null}
            </div>
            <aside className="sidebar">
              <WhereUsedPanel target="slide" id={s.id} resource={whereUsed} />
            </aside>
          </div>

          {dialog === 'upload' ? (
            <UploadVersionDialog
              slide={s}
              whereUsed={whereUsed}
              onClose={() => setDialog(null)}
              onSaved={refreshAll}
            />
          ) : null}
          {dialog === 'tags' ? (
            <TagEditorDialog
              target="slide"
              ids={[s.id]}
              initial={s.tags.map((t) => t.id)}
              title="Tags of this slide"
              onClose={() => setDialog(null)}
              onSaved={() => {
                setDialog(null);
                void slide.refresh();
              }}
            />
          ) : null}
          {dialog === 'retire' ? (
            <TypedConfirmDialog
              title="Retire slide"
              name={s.title}
              what="slide title"
              actionLabel="Retire slide"
              busyLabel="Retiring…"
              onClose={() => setDialog(null)}
              onConfirm={async (confirm) => {
                const updated = await retireSlide(s.id, confirm);
                slide.setData(updated);
                setDialog(null);
              }}
            >
              <p>
                A retired slide is hidden from the library unless “Show retired” is on. It can be unretired at any
                time. Retiring is refused while the slide is in the latest version of a group that is not retired.
              </p>
            </TypedConfirmDialog>
          ) : null}
        </div>
        );
      }}
    </DataView>
  );
}
